import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { supabase } from '@/integrations/supabase/client';
import { useLocation } from '@/providers/LocationProvider';
import { Download, MoreVertical } from 'lucide-react';
import { format } from 'date-fns';
import type { Booking } from '@/types';

export default function AdminBookings() {
  const location = useLocation();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();

    // Subscribe to realtime changes
    const channel = supabase
      .channel('admin-bookings')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'bookings' },
        () => fetchBookings()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [location]);

  const fetchBookings = async () => {
    try {
      const { data: locationData } = await supabase
        .from('locations')
        .select('id')
        .eq('code', location)
        .single();

      if (!locationData) return;

      const { data } = await supabase
        .from('bookings')
        .select(`
          *,
          customer:profiles!customer_id(full_name, phone),
          filmmaker:filmmakers!filmmaker_id(id)
        `)
        .eq('location_id', locationData.id)
        .order('scheduled_at', { ascending: false });

      setBookings((data as any) || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending_payment: 'bg-gray-500',
      paid: 'bg-yellow-500',
      assigned: 'bg-blue-500',
      en_route: 'bg-cyan-500',
      arrived: 'bg-purple-500',
      in_progress: 'bg-orange-500',
      awaiting_finish_confirm: 'bg-yellow-500',
      done_waiting_upload: 'bg-cyan-500',
      uploaded: 'bg-teal-500',
      completed: 'bg-green-500',
      canceled: 'bg-red-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Track Progress</h2>
          <p className="text-muted-foreground">
            Monitor all bookings and assignments
          </p>
        </div>
        <Button variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Booking ID</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Filmmaker</TableHead>
              <TableHead>Scheduled</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Total</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  Loading bookings...
                </TableCell>
              </TableRow>
            ) : bookings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  No bookings found
                </TableCell>
              </TableRow>
            ) : (
              bookings.map((booking) => (
                <TableRow key={booking.id}>
                  <TableCell className="font-mono text-xs">
                    {booking.id.slice(0, 8)}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{booking.customer?.full_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {booking.customer?.phone}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {booking.filmmaker_id ? (
                      <span className="text-sm">Assigned</span>
                    ) : (
                      <Button size="sm" variant="outline">
                        Assign
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {format(new Date(booking.scheduled_at), 'MMM d, h:mm a')}
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(booking.status)}>
                      {booking.status.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">
                    ${(booking.total_cents / 100).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>View Details</DropdownMenuItem>
                        <DropdownMenuItem>Reassign</DropdownMenuItem>
                        <DropdownMenuItem>Change Status</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive">
                          Cancel Booking
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
